/**
 * Launch / recovery copy for the mini-app's `LaunchGate`.
 *
 * ## Why this file exists
 *
 * These strings were hardcoded Russian inside `App.tsx`. `apps/mini-app` is detection-driven —
 * `main.tsx` calls `applyDocumentLocale()` bare — so an EN-resolved launch declared
 * `<html lang="en">` and then rendered Russian loading and recovery copy underneath it, on every
 * launch, before the main app appears. Found by product on `!509` (2026-08-31).
 *
 * ⭐ The rule is not "RU" or "EN": it is that ONE locale decision drives both the copy and
 * `<html lang>`. `getMessages()` defaults to the same `detectLocale()` the root resolves, so the
 * launch gate cannot disagree with the document it renders into.
 *
 * ⚠️ **The EN column is a translation of already-approved RU copy, not new claims** — same states,
 * same remedies, no capability or trust assertions added. It is still user-facing copy on an
 * honesty-sensitive surface (these are the strings a user sees when auth fails), so it wants a
 * product copy review, and `!509` asks for one explicitly rather than treating translation as
 * mechanical.
 *
 * ## The honesty constraint these strings already carry — do not flatten it
 *
 * `expired` and `forged` deliberately carry SEPARATE cause labels while sharing one remedy: calling
 * a forged payload "expired" is a false label and was fixed as an honesty-floor violation. The EN
 * mirror preserves that split. `no_launch_payload` has a DIFFERENT remedy (another Telegram
 * surface, not a relaunch) and must not be merged into the relaunch wording.
 */
import { detectLocale, type Locale } from '@agnivo/app-core';

export type LaunchMessages = {
  connecting: string;
  close: string;
  retry: string;
  transientFailure: string;
  terminal: {
    tgUidMissing: string;
    /** Takes the TD-code, which is diagnostic and identical in both locales. */
    noLaunchPayload: (code: string) => string;
    forged: string;
    expired: string;
  };
};

const ru: LaunchMessages = {
  connecting: 'Подключение к Telegram…',
  close: 'Закрыть',
  retry: 'Повторить',
  transientFailure: 'Не удалось подключиться. Проверьте соединение и повторите.',
  terminal: {
    tgUidMissing:
      'Ваша сессия предшествует обновлению безопасности. Закройте приложение и откройте его из Telegram заново, чтобы продолжить.',
    noLaunchPayload: (code) =>
      `Telegram не передал данные запуска мини-приложения. Откройте мини-приложение на телефоне или в Telegram Web (web.telegram.org). Код: ${code}`,
    forged:
      'Подпись не прошла проверку. Закройте приложение и откройте его из Telegram заново, чтобы продолжить.',
    expired: 'Сессия истекла. Закройте приложение и откройте его из Telegram заново, чтобы продолжить.',
  },
};

const en: LaunchMessages = {
  connecting: 'Connecting to Telegram…',
  close: 'Close',
  retry: 'Try again',
  transientFailure: 'Could not connect. Check your connection and try again.',
  terminal: {
    tgUidMissing:
      'Your session predates a security update. Close the app and open it again from Telegram to continue.',
    noLaunchPayload: (code) =>
      `Telegram did not pass the mini app's launch data. Open the mini app on your phone or in Telegram Web (web.telegram.org). Code: ${code}`,
    forged: 'The signature did not pass verification. Close the app and open it again from Telegram to continue.',
    expired: 'Your session has expired. Close the app and open it again from Telegram to continue.',
  },
};

const messages: Record<Locale, LaunchMessages> = { ru, en };

/**
 * ⛔ Defaults to `detectLocale()`, never to a locale literal.
 *
 * A literal default here would be a second locale decision the surface root cannot see — exactly
 * the shape that put Russian copy under `lang="en"`. `assert-document-locale-wired` fails any
 * dictionary under `apps/` that defaults to a literal, so this cannot quietly regress.
 */
export function getMessages(locale: Locale = detectLocale()): LaunchMessages {
  return messages[locale];
}
